import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type ContactSummary = {
  readonly id: number
  readonly email: string
  readonly first_name: string | null
  readonly last_name: string | null
  readonly company: string | null
  readonly replies_count: number
  readonly open_tasks_count: number
}

type ContactsIndexProps = {
  readonly contacts: readonly ContactSummary[]
  readonly filters: { readonly q: string; readonly show_archived?: string }
}

export default function ContactsIndex({ contacts, filters }: ContactsIndexProps) {
  const { props } = usePage<PageProps<ContactsIndexProps>>()
  const [q, setQ] = React.useState(filters.q)
  const [showArchived, setShowArchived] = React.useState(filters.show_archived === "1")
  const description = "Search and browse contacts across all clients, with reply and task counts at a glance."

  const search = (value: string, showArchivedValue?: boolean) => {
    setQ(value)
    const params: Record<string, string> = { q: value }
    const archived = showArchivedValue ?? showArchived
    if (archived) params.show_archived = "1"
    router.get(
      "/contacts",
      params,
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  const toggleArchived = () => {
    const next = !showArchived
    setShowArchived(next)
    search(q, next)
  }

  return (
    <>
      <Head title="Contacts">
        <meta name="description" content={description} />
        <meta property="og:title" content="Contacts" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Contacts</h1>
            <p className="mt-2">Every contact who has replied, with quick access to threads and tasks.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <div className="max-w-sm">
            <label htmlFor="contact-search" className="sr-only">
              Search contacts
            </label>
            <Input
              id="contact-search"
              placeholder="Search by name, company, or email…"
              value={q}
              onChange={(event) => search(event.target.value)}
            />
            <label className="mt-2 flex items-center gap-2 text-sm text-ink-muted">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={toggleArchived}
                className="rounded border-hairline"
              />
              Show archived-only contacts
            </label>
          </div>

          {contacts.length > 0 ? (
            <section className="overflow-hidden rounded-lg border border-hairline bg-page">
              <div className="divide-y divide-hairline">
                {contacts.map((contact) => (
                  <Link
                    key={contact.id}
                    href={`/contacts/${contact.id}`}
                    className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-ink-display">
                        {contactName(contact)}
                      </div>
                      {contact.company && (
                        <div className="mt-1 truncate text-sm text-ink-muted">
                          {contact.company}
                        </div>
                      )}
                    </div>
                    <div className="truncate text-sm text-ink-muted">
                      {contact.email}
                    </div>
                    <div className="flex gap-2 sm:justify-end">
                      <Badge tone="muted">{contact.replies_count} replies</Badge>
                      <Badge tone={contact.open_tasks_count > 0 ? "accent" : "muted"}>
                        {contact.open_tasks_count} open tasks
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ) : (
            <section className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center">
              <h2>No contacts found</h2>
              <p className="mt-2">
                {filters.q ? "Try a different search term." : "Contacts appear here once replies come in."}
              </p>
            </section>
          )}
        </div>
      </AppShell>
    </>
  )
}

function contactName(contact: ContactSummary) {
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
  return name || contact.email
}
